import { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

interface IPrintCountPoint {
  label: string;
  successful: number;
  failed: number;
  trendline: number;
}

interface IPrintCountComposedChartProps {
  data: IPrintCountPoint[];
  successColor: string;
  failedColor: string;
  trendlineColor: string;
}

const PrintCountComposedChart = ({
  data,
  successColor,
  failedColor,
  trendlineColor,
}: IPrintCountComposedChartProps) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<am5.Root | null>(null);

  useLayoutEffect(() => {
    if (!chartRef.current) {
      return;
    }

    rootRef.current?.dispose();

    const root = am5.Root.new(chartRef.current);
    rootRef.current = root;
    root._logo?.dispose();

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
      })
    );

    const legend = chart.children.unshift(
      am5.Legend.new(root, {
        x: am5.p100,
        centerX: am5.p100,
        useDefaultMarker: true,
      })
    );

    legend.labels.template.setAll({
      fontSize: 12,
      fill: am5.color(0x63738a),
    });

    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 22,
    });

    xRenderer.labels.template.setAll({
      rotation: -45,
      centerX: am5.p100,
      centerY: am5.p100,
      fill: am5.color(0x63738a),
      fontSize: 11,
      paddingTop: 10,
      oversizedBehavior: 'truncate',
      maxWidth: 95,
    });

    xRenderer.grid.template.setAll({
      visible: false,
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'label',
        renderer: xRenderer,
      })
    );

    xAxis.data.setAll(data);

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({
      fill: am5.color(0x63738a),
      fontSize: 11,
    });

    yRenderer.grid.template.setAll({
      stroke: am5.color(0xebeff3),
      strokeOpacity: 1,
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        strictMinMax: false,
        renderer: yRenderer,
      })
    );

    const successfulSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Successful Print',
        xAxis,
        yAxis,
        valueYField: 'successful',
        categoryXField: 'label',
        stacked: true,
      })
    );

    successfulSeries.columns.template.setAll({
      fill: am5.color(successColor),
      strokeOpacity: 0,
      width: am5.percent(72),
      tooltipText: '{name}: {valueY.formatNumber("#,###")}',
    });

    successfulSeries.data.setAll(data);

    const failedSeries = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Failed Print',
        xAxis,
        yAxis,
        valueYField: 'failed',
        categoryXField: 'label',
        stacked: true,
      })
    );

    failedSeries.columns.template.setAll({
      fill: am5.color(failedColor),
      strokeOpacity: 0,
      width: am5.percent(72),
      tooltipText: '{name}: {valueY.formatNumber("#,###")}',
    });

    failedSeries.data.setAll(data);

    const trendlineSeries = chart.series.push(
      am5xy.LineSeries.new(root, {
        name: 'Trendline',
        xAxis,
        yAxis,
        valueYField: 'trendline',
        categoryXField: 'label',
        stroke: am5.color(trendlineColor),
      })
    );

    trendlineSeries.strokes.template.setAll({
      strokeWidth: 2,
      stroke: am5.color(trendlineColor),
    });

    trendlineSeries.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 3,
          fill: am5.color(trendlineColor),
        }),
      });
    });

    trendlineSeries.data.setAll(data);
    legend.data.setAll([successfulSeries, failedSeries, trendlineSeries]);

    return () => {
      root.dispose();
      rootRef.current = null;
    };
  }, [data, successColor, failedColor, trendlineColor]);

  return (
    <div
      className="amcharts-wrapper"
      data-testid="composed-chart"
      ref={chartRef}
      style={{ width: '100%', height: '420px' }}
    />
  );
};

export default PrintCountComposedChart;
